import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Card, CardContent, Typography, TextField, Button, Switch,
    List, ListItem, ListItemText, IconButton, Stack, CircularProgress, Divider,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import api from "../axiosConfig.js";

export default function MassEmailMonitoringSection({ showSnackbar }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");
    const [label, setLabel] = useState("");
    const [saving, setSaving] = useState(false);
    const [busyIds, setBusyIds] = useState(() => new Set());

    const setBusy = (id, on) => {
        setBusyIds((prev) => {
            const next = new Set(prev);
            if (on) next.add(id); else next.delete(id);
            return next;
        });
    };

    const onEnter = (e) => { if (e.key === "Enter") handleAdd(); };

    const notify = useCallback((msg, sev = "success") => {
        if (typeof showSnackbar === "function") showSnackbar(msg, sev);
    }, [showSnackbar]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/mass-email-monitors");
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            notify("Failed to load monitoring addresses.", "error");
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        const trimmed = email.trim();
        if (!trimmed) return;
        setSaving(true);
        try {
            await api.post("/admin/mass-email-monitors", { email: trimmed, label: label.trim() || null });
            setEmail(""); setLabel("");
            notify("Monitoring address added.");
            await load();
        } catch (e) {
            const msg = e?.response?.status === 422
                ? "That email is invalid or already on the list."
                : "Failed to add address.";
            notify(msg, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (row) => {
        if (busyIds.has(row.id)) return;
        setBusy(row.id, true);
        // Optimistically flip so the Switch responds instantly.
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, active: !r.active } : r)));
        try {
            await api.patch(`/admin/mass-email-monitors/${row.id}`, { active: !row.active });
        } catch (e) {
            notify("Failed to update address.", "error");
            await load(); // restore authoritative state on failure
        } finally {
            setBusy(row.id, false);
        }
    };

    const handleDelete = async (row) => {
        if (busyIds.has(row.id)) return;
        if (!window.confirm(`Remove ${row.email} from monitoring?`)) return;
        setBusy(row.id, true);
        try {
            await api.delete(`/admin/mass-email-monitors/${row.id}`);
            notify("Monitoring address removed.");
            await load();
        } catch (e) {
            notify("Failed to remove address.", "error");
        } finally {
            setBusy(row.id, false);
        }
    };

    return (
        <Card sx={{ maxWidth: 720, mx: "auto" }}>
            <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Mass Email Monitoring</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    These addresses receive one <strong>[Monitor]</strong>-prefixed copy of every mass email
                    campaign that goes out. Copies carry no open/click tracking, so they never affect your stats.
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        label="Email address" size="small" fullWidth value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={onEnter}
                    />
                    <TextField
                        label="Label (optional)" size="small" value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        onKeyDown={onEnter}
                        sx={{ minWidth: { sm: 180 } }}
                    />
                    <Button variant="contained" onClick={handleAdd} disabled={saving || !email.trim()}>
                        Add
                    </Button>
                </Stack>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={28} /></Box>
                ) : rows.length === 0 ? (
                    <Typography sx={{ textAlign: "center", py: 3, fontStyle: "italic" }} color="text.secondary">
                        No monitoring addresses yet. Add one above to start receiving copies.
                    </Typography>
                ) : (
                    <List>
                        {rows.map((row) => (
                            <ListItem
                                key={row.id}
                                secondaryAction={
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Switch
                                            checked={!!row.active}
                                            onChange={() => handleToggle(row)}
                                            disabled={busyIds.has(row.id)}
                                            inputProps={{ "aria-label": `Toggle monitoring for ${row.email}` }}
                                        />
                                        <IconButton
                                            edge="end"
                                            color="error"
                                            onClick={() => handleDelete(row)}
                                            disabled={busyIds.has(row.id)}
                                            aria-label={`Remove ${row.email}`}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Stack>
                                }
                            >
                                <ListItemText
                                    primary={row.email}
                                    secondary={[row.label, row.active ? "Active" : "Inactive"].filter(Boolean).join(" · ")}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </CardContent>
        </Card>
    );
}
